/**
 * Statistics Dashboard Page
 *
 * Displays platform-wide statistics and analytics including:
 * - Monthly/yearly revocations (carriers and brokers)
 * - Active carrier/broker counts
 * - Crash trends with fatal/injury breakdown
 * - Inspection trends
 * - Violation trends by BASIC category
 * - State-by-state breakdowns
 * - Interactive charts
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, TrendingDown, AlertTriangle,
  FileText, Shield, MapPin, Calendar, Activity,
  Users, XCircle
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { StatStrip } from '../components/shared/StatStrip';
import { statsApi } from '../services/api';
import '../styles/modern.css';

interface DashboardStats {
  overview: {
    activeCarriers: number;
    activeBrokers: number;
    crashesThisMonth: number;
    crashesThisYear: number;
    fatalitiesThisMonth: number;
    fatalitiesThisYear: number;
    inspectionsThisMonth: number;
    inspectionsThisYear: number;
    carriersRevoked: number;
    brokersRevoked: number;
  };
  monthlyActivity: Array<{
    month: string;
    crashes: number;
    inspections: number;
    violations: number;
  }>;
  topStates: Array<{
    state: string;
    activityScore: number;
  }>;
}

interface CrashTrends {
  monthly: Array<{
    month: string;
    total: number;
    fatalities: number;
    injuries: number;
    towaway: number;
  }>;
  currentMonth: {
    crashes: number;
    fatalities: number;
    injuries: number;
  };
  currentYear: {
    crashes: number;
    fatalities: number;
    injuries: number;
  };
  severityBreakdown: Array<{
    severity: string;
    count: number;
  }>;
}

interface ViolationTrends {
  byCategory: Array<{
    category: string;
    count: number;
    oosCount: number;
  }>;
  topViolations: Array<{
    code: string;
    count: number;
    oosCount: number;
  }>;
  monthly: Array<{
    month: string;
    total: number;
    oos: number;
  }>;
}

const StatsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [crashTrends, setCrashTrends] = useState<CrashTrends | null>(null);
  const [violationTrends, setViolationTrends] = useState<ViolationTrends | null>(null);
  const [carrierBreakdown, setCarrierBreakdown] = useState<any | null>(null);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const navigateToCarrierList = (classification: string) => {
    navigate(`/market-intelligence?carrierClassification=${classification}`);
  };

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all statistics in parallel using statsApi
      const [dashboard, crashes, violations, breakdown] = await Promise.all([
        statsApi.getDashboard(),
        statsApi.getCrashTrends(),
        statsApi.getViolationTrends(),
        statsApi.getCarrierBreakdown(),
      ]);

      setDashboardStats(dashboard as unknown as DashboardStats);
      setCrashTrends(crashes as unknown as CrashTrends);
      setViolationTrends(violations as unknown as ViolationTrends);
      setCarrierBreakdown(breakdown);
    } catch (err: unknown) {
      console.error('Error fetching statistics:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load statistics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = {
    primary: '#23211d',   // ink
    success: '#5d7148',   // moss
    warning: '#b07d2b',   // ochre
    danger: '#b3412c',    // vermilion (literal: feeds Recharts props, var() won't resolve)
    purple: '#4a463f',    // no purple in Washi — neutral ink-gray
    teal: '#6d685e',      // no teal in Washi — neutral gray
  };

  const SEVERITY_COLORS: Record<string, string> = {
    fatal: COLORS.danger,
    injury: COLORS.warning,
    towaway: COLORS.primary,
    other: 'var(--color-gray-400)',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <div style={{ textAlign: 'center' }}>
            <LoadingSpinner message="Loading statistics..." size="lg" />
            <p style={{ marginTop: 'var(--space-lg)', color: 'var(--color-gray-500)' }}>Loading platform statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="card-modern" style={{ borderLeft: '4px solid var(--color-danger)' }}>
            <p style={{ color: 'var(--color-danger)' }}>Error: {error}</p>
            <button onClick={fetchAllStats} className="btn-primary" style={{ marginTop: 'var(--space-md)' }}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-2xl)' }}>
          <h1 className="heading-1" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <BarChart3 style={{ width: 32, height: 32, color: 'var(--color-primary)' }} />
            Platform Statistics
          </h1>
          <p className="text-muted" style={{ marginTop: 'var(--space-sm)', fontSize: '1rem' }}>
            Real-time analytics across 28.5M government records
          </p>
        </div>

        {/* Overview Cards */}
        {dashboardStats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {/* Active Carriers */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <Users style={{ width: 24, height: 24, color: 'var(--color-primary)' }} />
                <div className="badge-modern badge-primary">{dashboardStats.overview.activeCarriers.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Active Carriers</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>Operating authority</p>
            </div>

            {/* Active Brokers */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <Shield style={{ width: 24, height: 24, color: 'var(--color-success)' }} />
                <div className="badge-modern badge-success">{dashboardStats.overview.activeBrokers.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Active Brokers</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>Broker authority</p>
            </div>

            {/* Revoked Carriers */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <XCircle style={{ width: 24, height: 24, color: 'var(--color-danger)' }} />
                <div className="badge-modern badge-danger">{dashboardStats.overview.carriersRevoked.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Carriers Revoked</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>Authority revoked</p>
            </div>

            {/* Revoked Brokers */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <XCircle style={{ width: 24, height: 24, color: 'var(--color-danger)' }} />
                <div className="badge-modern badge-danger">{dashboardStats.overview.brokersRevoked.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Brokers Revoked</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>Broker authority revoked</p>
            </div>

            {/* Crashes This Year */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <AlertTriangle style={{ width: 24, height: 24, color: 'var(--color-warning)' }} />
                <div className="badge-modern badge-warning">{dashboardStats.overview.crashesThisYear.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Crashes (YTD)</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
                {dashboardStats.overview.fatalitiesThisYear} fatal, This month: {dashboardStats.overview.crashesThisMonth}
              </p>
            </div>

            {/* Inspections This Year */}
            <div className="card-modern">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <FileText style={{ width: 24, height: 24, color: 'var(--color-primary)' }} />
                <div className="badge-modern badge-info">{dashboardStats.overview.inspectionsThisYear.toLocaleString()}</div>
              </div>
              <h3 className="heading-3">Inspections (YTD)</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
                This month: {dashboardStats.overview.inspectionsThisMonth.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Carrier Breakdown Section */}
        {carrierBreakdown && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Users style={{ width: 24, height: 24, color: 'var(--color-primary)' }} />
              Carrier Classification Breakdown
            </h2>

            {/* By Authority Status & Operation Scope */}
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <h3 className="heading-3" style={{ marginBottom: 'var(--space-lg)' }}>By Authority Status & Operation Scope</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
                {/* For-Hire Interstate */}
                <div
                  className="card-modern"
                  style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-primary)', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  onClick={() => navigateToCarrierList('for_hire_interstate')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.forHire?.interstate?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🌎 For-Hire Interstate</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    For-hire, cross state lines
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>

                {/* For-Hire Intrastate Non-Hazmat */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-gray-600)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('for_hire_intrastate_non_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-600)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.forHire?.intrastateNonHazmat?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🏙️ For-Hire Intrastate</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>For-hire, within state (non-hazmat)</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>

                {/* For-Hire Intrastate Hazmat */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-gray-700)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('for_hire_intrastate_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-700)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.forHire?.intrastateHazmat?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>☢️ For-Hire Hazmat</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>For-hire, intrastate hazmat</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-700)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>

                {/* Private Interstate */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-success)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_interstate')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.private?.interstate?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🌎 Private Interstate</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>Private, cross state lines</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>

                {/* Private Intrastate Non-Hazmat */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-warning)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_intrastate_non_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.private?.intrastateNonHazmat?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🏙️ Private Intrastate</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>Private, within state (non-hazmat)</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>

                {/* Private Intrastate Hazmat */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-danger)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_intrastate_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-danger)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.byAuthority?.private?.intrastateHazmat?.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>☢️ Private Hazmat</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>Private, intrastate hazmat</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-danger)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                </div>
              </div>
            </div>

            {/* Insurance Cancellations - CRITICAL SECTION */}
            {carrierBreakdown.insuranceCancellations && (carrierBreakdown.insuranceCancellations.totalPendingCancellation > 0 || carrierBreakdown.insuranceCancellations.totalCritical30Days > 0) && (
              <div style={{ marginBottom: 'var(--space-xl)', padding: 'var(--space-lg)', background: '#f1e2da', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-danger)' }}>
                <h3 className="heading-3" style={{ marginBottom: 'var(--space-lg)', color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <AlertTriangle style={{ width: 24, height: 24 }} />
                  Insurance Pending Cancellation — Critical Alert
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
                  {/* Critical - Cancelling within 30 days */}
                  <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-danger)', cursor: 'pointer', transition: 'transform 0.2s', background: '#fbf9f4' }} onClick={() => navigateToCarrierList('insurance_cancelling_30days')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-danger)', marginBottom: 'var(--space-xs)' }}>
                      {carrierBreakdown.insuranceCancellations.totalCritical30Days?.toLocaleString()}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)', color: 'var(--color-danger)' }}>🚨 Cancelling Within 30 Days</div>
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Immediate action required
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-danger)', marginTop: 'var(--space-sm)' }}>Click to view critical list →</div>
                  </div>

                  {/* BIPD Pending Cancellation */}
                  <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-warning)', cursor: 'pointer', transition: 'transform 0.2s', background: '#fbf9f4' }} onClick={() => navigateToCarrierList('insurance_pending_cancellation')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--space-xs)' }}>
                      {carrierBreakdown.insuranceCancellations.bipdPendingCancellation?.toLocaleString()}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>⚠️ BIPD Posted for Cancellation</div>
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Liability insurance
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                  </div>

                  {/* Cargo Pending Cancellation */}
                  {carrierBreakdown.insuranceCancellations.cargoPendingCancellation > 0 && (
                    <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-warning)', cursor: 'pointer', transition: 'transform 0.2s', background: '#fbf9f4' }} onClick={() => navigate('/market-intelligence?insuranceType=cargo&insuranceExpiringDays=365')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--space-xs)' }}>
                        {carrierBreakdown.insuranceCancellations.cargoPendingCancellation?.toLocaleString()}
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>⚠️ Cargo Posted for Cancellation</div>
                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                        Cargo insurance
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: 'var(--space-sm)' }}>Click to view list →</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* For-Hire Breakdown */}
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <h3 className="heading-3" style={{ marginBottom: 'var(--space-lg)' }}>For-Hire Carrier Types</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
                {/* Property Carriers */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-primary)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('property_carriers')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.forHireBreakdown?.propertyCarriers?.total.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🚛 Property Carriers</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {carrierBreakdown.forHireBreakdown?.propertyCarriers?.description}
                  </div>
                  <div style={{ marginTop: 'var(--space-sm)', fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                    Click to view list →
                  </div>
                </div>

                {/* Bus Companies */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-success)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('bus_companies')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.forHireBreakdown?.busCompanies?.total.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🚌 Bus Companies</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {carrierBreakdown.forHireBreakdown?.busCompanies?.description}
                  </div>
                  <div style={{ marginTop: 'var(--space-sm)', fontSize: '0.875rem', color: 'var(--color-success)' }}>
                    Click to view list →
                  </div>
                </div>

                {/* Moving Companies */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-warning)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('moving_companies')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.forHireBreakdown?.movingCompanies?.total.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>🏠 Moving Companies</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {carrierBreakdown.forHireBreakdown?.movingCompanies?.description}
                  </div>
                  <div style={{ marginTop: 'var(--space-sm)', fontSize: '0.875rem', color: 'var(--color-warning)' }}>
                    Click to view list →
                  </div>
                </div>

                {/* Brokers */}
                <div className="card-modern" style={{ padding: 'var(--space-lg)', borderLeft: '4px solid var(--color-purple)', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('brokers')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-purple)', marginBottom: 'var(--space-xs)' }}>
                    {carrierBreakdown.forHireBreakdown?.brokers?.total.toLocaleString()}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>📦 Brokers</div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {carrierBreakdown.forHireBreakdown?.brokers?.description}
                  </div>
                  <div style={{ marginTop: 'var(--space-sm)', fontSize: '0.875rem', color: 'var(--color-purple)' }}>
                    Click to view list →
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Activity Chart */}
        {dashboardStats && dashboardStats.monthlyActivity.length > 0 && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Activity style={{ width: 24, height: 24, color: 'var(--color-primary)' }} />
              Monthly Activity Trends (Last 12 Months)
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={[...dashboardStats.monthlyActivity].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#857f73', fontSize: 12 }}
                  tickFormatter={(value) => value.substring(5)} // Show MM only
                />
                <YAxis tick={{ fill: '#857f73', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fbf9f4',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="crashes"
                  stroke={COLORS.danger}
                  strokeWidth={2}
                  name="Crashes"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="inspections"
                  stroke={COLORS.primary}
                  strokeWidth={2}
                  name="Inspections"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="violations"
                  stroke={COLORS.warning}
                  strokeWidth={2}
                  name="Violations"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Crash Trends */}
        {crashTrends && crashTrends.monthly.length > 0 && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <AlertTriangle style={{ width: 24, height: 24, color: 'var(--color-danger)' }} />
              Crash Trends by Severity
            </h2>

            {/* Current Stats */}
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <StatStrip cells={[
                { key: 'cm', label: 'Crashes This Month', value: crashTrends.currentMonth.crashes, tone: 'warn' },
                { key: 'fm', label: 'Fatalities This Month', value: crashTrends.currentMonth.fatalities, tone: 'risk' },
                { key: 'cy', label: 'Crashes Year-to-Date', value: crashTrends.currentYear.crashes, tone: 'warn' },
                { key: 'fy', label: 'Fatalities Year-to-Date', value: crashTrends.currentYear.fatalities, tone: 'risk' },
              ]} />
            </div>

            {/* Monthly Crash Chart */}
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={[...crashTrends.monthly].reverse().slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#857f73', fontSize: 12 }}
                  tickFormatter={(value) => value.substring(5)}
                />
                <YAxis tick={{ fill: '#857f73', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fbf9f4',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="fatalities" fill={COLORS.danger} name="Fatal" stackId="a" />
                <Bar dataKey="injuries" fill={COLORS.warning} name="Injury" stackId="a" />
                <Bar dataKey="towaway" fill={COLORS.primary} name="Towaway" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Violation Trends by BASIC Category */}
        {violationTrends && violationTrends.byCategory.length > 0 && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Shield style={{ width: 24, height: 24, color: 'var(--color-warning)' }} />
              Violations by BASIC Category
            </h2>

            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={violationTrends.byCategory} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis type="number" tick={{ fill: '#857f73', fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="category"
                  tick={{ fill: '#857f73', fontSize: 12 }}
                  width={180}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fbf9f4',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="count" fill={COLORS.primary} name="Total Violations" />
                <Bar dataKey="oosCount" fill={COLORS.danger} name="Out-of-Service" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top 10 States */}
        {dashboardStats && dashboardStats.topStates.length > 0 && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <MapPin style={{ width: 24, height: 24, color: 'var(--color-success)' }} />
              Top 10 States by Activity
            </h2>

            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardStats.topStates}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis
                  dataKey="state"
                  tick={{ fill: '#857f73', fontSize: 12 }}
                />
                <YAxis tick={{ fill: '#857f73', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fbf9f4',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="activityScore" fill={COLORS.success} name="Activity Score" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Violations Table */}
        {violationTrends && violationTrends.topViolations.length > 0 && (
          <div className="card-modern">
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <FileText style={{ width: 24, height: 24, color: 'var(--color-warning)' }} />
              Top 10 Most Common Violations
            </h2>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--color-gray-700)', fontSize: '0.875rem', fontWeight: 600 }}>
                      Violation Code
                    </th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--color-gray-700)', fontSize: '0.875rem', fontWeight: 600 }}>
                      Total Count
                    </th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--color-gray-700)', fontSize: '0.875rem', fontWeight: 600 }}>
                      Out-of-Service
                    </th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--color-gray-700)', fontSize: '0.875rem', fontWeight: 600 }}>
                      OOS Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {violationTrends.topViolations.map((violation, idx) => (
                    <tr key={violation.code} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      <td style={{ padding: 'var(--space-md)' }}>
                        <code className="badge-modern badge-info" style={{ fontFamily: 'monospace' }}>
                          {violation.code}
                        </code>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'right', fontSize: '0.9375rem', fontWeight: 600 }}>
                        {violation.count.toLocaleString()}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'right' }}>
                        <span className="badge-modern badge-danger">
                          {violation.oosCount.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--color-gray-600)' }}>
                        {((violation.oosCount / violation.count) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsDashboard;
